import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, UserPlus, Clock, Mail } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import EmptyState from "@/components/common/EmptyState";
import Avatar from "@/components/common/Avatar";
import { listPendingLinks, setLinkStatus } from "@/lib/db";
import { formatDateTime } from "@/lib/format";

export default function Vinculacoes() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const data = await listPendingLinks();
      setList(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    await setLinkStatus(id, status);
    load();
  };

  const filtered = list.filter(l => filter === "all" ? true : l.status === filter);
  const pendingCount = list.filter(l => l.status === "pending").length;

  return (
    <div className="ds-fade-up">
      <PageHeader
        eyebrow="Vínculos"
        title="Solicitações de pacientes"
        description="Aprove pacientes que solicitaram vínculo com você. Convites por código são aprovados automaticamente."
      />
      <div className="ds-card p-3 mb-5 flex items-center gap-2">
        {[
          { v: "pending", l: `Pendentes${pendingCount ? ` · ${pendingCount}` : ""}` },
          { v: "active", l: "Ativos" },
          { v: "blocked", l: "Bloqueados" },
          { v: "all", l: "Todos" },
        ].map(t => (
          <button key={t.v} data-testid={`vinc-filter-${t.v}`} onClick={() => setFilter(t.v)}
            className={`px-3 h-9 rounded-md text-xs font-medium transition-colors ${
              filter === t.v ? "bg-[hsl(var(--brand))] text-white" : "text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--bg-hover))]"
            }`}>
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="ds-card p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando...</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={UserPlus}
          title={filter === "pending" ? "Nenhuma solicitação pendente" : "Nada por aqui"}
          description={filter === "pending" ? "Compartilhe um código de convite para acelerar a vinculação." : "Tente outro filtro."}
          action={filter === "pending" && (
            <Link to="/app/convites" className="ds-btn-primary">
              <Mail className="w-4 h-4" /> Gerar convite
            </Link>
          )}
        />
      ) : (
        <>
          {/* Mobile View: Cards */}
          <div className="grid grid-cols-1 gap-4 md:hidden" data-testid="vinc-cards">
            {filtered.map(l => (
              <div key={l.id} className="ds-card p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar name={l.patient?.full_name || "?"} size={48} />
                    <div>
                      <div className="text-base font-bold" style={{fontFamily:"'Outfit',sans-serif"}}>{l.patient?.full_name || "—"}</div>
                      <div className="text-[10px] text-[hsl(var(--text-tertiary))] uppercase font-bold tracking-tight">{l.patient?.email || "—"}</div>
                    </div>
                  </div>
                  {l.status === "pending" && <span className="ds-badge ds-badge-warning">Pendente</span>}
                  {l.status === "active" && <span className="ds-badge ds-badge-success">Ativo</span>}
                  {l.status === "blocked" && <span className="ds-badge ds-badge-danger">Bloqueado</span>}
                </div>
                
                <div className="flex items-center gap-2 pt-2">
                  <span className="text-[10px] text-[hsl(var(--text-tertiary))] font-bold uppercase tracking-widest">
                    Via {l.source === "invite" ? "Convite" : "Busca"} · {formatDateTime(l.requested_at)}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  {l.status === "pending" && (
                    <>
                      <button onClick={() => decide(l.id, "active")} className="ds-btn-primary h-11 px-4 text-sm font-bold">
                        Aprovar
                      </button>
                      <button onClick={() => decide(l.id, "blocked")} className="ds-btn-secondary h-11 px-4 text-sm font-bold text-[hsl(var(--danger-text))] border-[hsl(var(--danger-text))]/30">
                        Recusar
                      </button>
                    </>
                  )}
                  {l.status === "active" && (
                    <Link to={`/app/pacientes/${l.patient?.id}`} className="ds-btn-secondary col-span-2 h-11 px-4 text-sm font-bold flex items-center justify-center">
                      Abrir prontuário
                    </Link>
                  )}
                  {l.status === "blocked" && (
                    <button onClick={() => decide(l.id, "pending")} className="ds-btn-ghost col-span-2 h-11 px-4 text-sm font-bold flex items-center justify-center gap-2">
                      <Clock className="w-4 h-4" /> Reativar solicitação
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View: List */}
          <ul className="hidden md:block space-y-3" data-testid="vinc-list">
            {filtered.map(l => (
              <li key={l.id} className="ds-card p-5 flex items-center gap-4">
                <Avatar name={l.patient?.full_name || "?"} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{l.patient?.full_name || "—"}</span>
                    {l.source === "invite" ? (
                      <span className="ds-badge ds-badge-info">via convite</span>
                    ) : (
                      <span className="ds-badge ds-badge-neutral">via busca</span>
                    )}
                    {l.status === "pending" && <span className="ds-badge ds-badge-warning">Pendente</span>}
                    {l.status === "active" && <span className="ds-badge ds-badge-success">Ativo</span>}
                    {l.status === "blocked" && <span className="ds-badge ds-badge-danger">Bloqueado</span>}
                  </div>
                  <div className="text-xs text-[hsl(var(--text-tertiary))] mt-0.5">
                    {l.patient?.email || "—"} · solicitado {formatDateTime(l.requested_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {l.status === "pending" && (
                    <>
                      <button data-testid={`vinc-approve-${l.id}`} onClick={() => decide(l.id, "active")} className="ds-btn-primary h-9 px-3">
                        <Check className="w-4 h-4" /> Aprovar
                      </button>
                      <button data-testid={`vinc-block-${l.id}`} onClick={() => decide(l.id, "blocked")} className="ds-btn-secondary h-9 px-3 text-[hsl(var(--danger-text))] border-[hsl(var(--danger-text))]/30">
                        <X className="w-4 h-4" /> Recusar
                      </button>
                    </>
                  )}
                  {l.status === "active" && (
                    <Link to={`/app/pacientes/${l.patient?.id}`} className="ds-btn-secondary h-9 px-3">Abrir prontuário</Link>
                  )}
                  {l.status === "blocked" && (
                    <button onClick={() => decide(l.id, "pending")} className="ds-btn-ghost">
                      <Clock className="w-4 h-4" /> Reabrir
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
